import { useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { invalidateIndicatorCaches } from "@/lib/cache-invalidation";
import type { DocumentPage, PageIndicator } from "@shared/schema";

interface UseIndicatorMutationsProps {
  currentPageData?: DocumentPage;
  activeIndicator: PageIndicator | null;
  setActiveIndicator: (indicator: PageIndicator | null) => void;
  setPopoverPosition: (position: { x: number; y: number } | null) => void;
  documentId: string;
  versionNumber: number;
  versionId?: string;
  // Drawing box state for cleanup
  indicatorDrawingBoxes: Map<number, { startX: number; startY: number; endX: number; endY: number }>;
  setIndicatorDrawingBoxes: React.Dispatch<React.SetStateAction<Map<number, { startX: number; startY: number; endX: number; endY: number }>>>;
  tempDrawingBox?: { startX: number; startY: number; endX: number; endY: number } | null;
  setTempDrawingBox?: (box: { startX: number; startY: number; endX: number; endY: number } | null) => void;
}

export function useIndicatorMutations({
  currentPageData,
  activeIndicator,
  setActiveIndicator,
  setPopoverPosition,
  documentId,
  versionNumber,
  versionId,
  indicatorDrawingBoxes,
  setIndicatorDrawingBoxes,
  tempDrawingBox,
  setTempDrawingBox,
}: UseIndicatorMutationsProps) {
  
  // Helper function to clean up drawing box state when an indicator is deleted
  const cleanupDrawingBoxState = (deletedIndicatorId: string, indicatorNumber?: number) => {
    // Clear indicator-specific drawing box from state if we have the indicator number
    if (indicatorNumber) {
      setIndicatorDrawingBoxes((prev: Map<number, { startX: number; startY: number; endX: number; endY: number }>) => {
        const newMap = new Map(prev);
        newMap.delete(indicatorNumber);
        return newMap;
      });
      
      // FIXED: Update localStorage using read-modify-write pattern to prevent data loss
      if (currentPageData?.id) {
        const storageKey = `indicatorDrawingBoxes_${currentPageData.id}`;
        const savedBoxes = JSON.parse(localStorage.getItem(storageKey) || '{}');
        delete savedBoxes[indicatorNumber];
        localStorage.setItem(storageKey, JSON.stringify(savedBoxes));
        console.log(`🔧 FIXED: Used read-modify-write pattern for localStorage to prevent data loss`);
      }
    }
    
    // Clear temporary drawing box state
    if (setTempDrawingBox) {
      setTempDrawingBox(null);
    }
    
    console.log(`🧹 Cleaned up drawing box state for deleted indicator: ${deletedIndicatorId} (number: ${indicatorNumber})`);
  };
  const updateIndicatorMutation = useMutation({
    mutationFn: async ({ indicatorId, newPosition }: { indicatorId: string; newPosition: { x: number; y: number } }) => {
      // Check if indicator has comments before allowing position update
      const commentsResponse = await fetch(`/api/indicators/${indicatorId}/comments`);
      const existingComments = await commentsResponse.json();
      
      if (!existingComments || existingComments.length === 0) {
        // No comments = don't save position, delete the indicator instead
        const deleteResponse = await fetch(`/api/pages/${currentPageData?.id}/indicators/${indicatorId}`, {
          method: "DELETE",
        });
        if (!deleteResponse.ok) throw new Error("Failed to delete indicator without comments");
        throw new Error("INDICATOR_DELETED"); // Special error to handle deletion
      }

      const response = await fetch(`/api/indicators/${indicatorId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newPosition),
      });
      if (!response.ok) throw new Error("Failed to update indicator");
      return response.json();
    },
    onMutate: async ({ indicatorId, newPosition }) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ["/api/pages", currentPageData?.id, "indicators"] });

      // Snapshot the previous value
      const previousIndicators = queryClient.getQueryData<PageIndicator[]>(["/api/pages", currentPageData?.id, "indicators"]);

      // Optimistically update to the new value
      queryClient.setQueryData<PageIndicator[]>(
        ["/api/pages", currentPageData?.id, "indicators"],
        (old) => {
          if (!old) return old;
          return old.map(indicator => 
            indicator.id === indicatorId 
              ? { ...indicator, x: newPosition.x, y: newPosition.y }
              : indicator
          );
        }
      );

      // Return a context object with the snapshotted value
      return { previousIndicators };
    },
    onError: (err, variables, context) => {
      // Handle special case where indicator was deleted due to no comments
      if (err.message === "INDICATOR_DELETED") {
        // Clear active indicator state and refresh both caches
        setActiveIndicator(null);
        setPopoverPosition(null);
        
        // Clean up drawing box state since indicator was deleted - find indicator number first
        const deletedIndicator = context?.previousIndicators?.find(ind => ind.id === variables.indicatorId);
        cleanupDrawingBoxState(variables.indicatorId, deletedIndicator?.number);
        
        if (currentPageData) {
          invalidateIndicatorCaches(currentPageData.id, documentId, versionNumber, versionId);
        }
        return;
      }
      
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousIndicators) {
        queryClient.setQueryData(
          ["/api/pages", currentPageData?.id, "indicators"],
          context.previousIndicators
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the server state
      if (currentPageData) {
        invalidateIndicatorCaches(currentPageData.id, documentId, versionNumber, versionId);
      }
    }
  });

  const deleteIndicatorMutation = useMutation({
    mutationFn: async (indicatorId: string) => {
      const response = await fetch(`/api/pages/${currentPageData?.id}/indicators/${indicatorId}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete indicator");
      return indicatorId; // Return the deleted indicator ID for cleanup
    },
    onMutate: async (indicatorId: string) => {
      // OPTIMISTIC UPDATE: Immediately remove indicator from cache before server deletion
      await queryClient.cancelQueries({ queryKey: ["/api/pages", currentPageData?.id, "indicators"] });
      
      const previousIndicators = queryClient.getQueryData<PageIndicator[]>(["/api/pages", currentPageData?.id, "indicators"]);
      
      queryClient.setQueryData<PageIndicator[]>(
        ["/api/pages", currentPageData?.id, "indicators"],
        (old) => old?.filter(indicator => indicator.id !== indicatorId) || []
      );
      
      console.log(`🚀 Optimistically removed indicator ${indicatorId} from UI`);
      return { previousIndicators };
    },
    onSuccess: (deletedIndicatorId) => {
      setActiveIndicator(null);
      setPopoverPosition(null);
      
      // Clean up drawing box state for the deleted indicator - find indicator number first
      const indicatorsBeforeDeletion = queryClient.getQueryData<PageIndicator[]>(['/api/pages', currentPageData?.id, 'indicators']);
      const deletedIndicator = indicatorsBeforeDeletion?.find(ind => ind.id === deletedIndicatorId);
      cleanupDrawingBoxState(deletedIndicatorId, deletedIndicator?.number);
      
      if (currentPageData) {
        invalidateIndicatorCaches(currentPageData.id, documentId, versionNumber, versionId);
      }
    },
    onError: (error, variables, context) => {
      // ROLLBACK: Restore indicators on error
      if (context?.previousIndicators) {
        queryClient.setQueryData(
          ["/api/pages", currentPageData?.id, "indicators"],
          context.previousIndicators
        );
      }
    }
  });

  const updateIndicatorStatusMutation = useMutation({
    mutationFn: async ({ indicatorId, status }: { indicatorId: string; status: string }) => {
      const response = await fetch(`/api/indicators/${indicatorId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) throw new Error("Failed to update indicator status");
      return response.json();
    },
    onMutate: async ({ indicatorId, status }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["/api/pages", currentPageData?.id, "indicators"] });

      // Snapshot the previous value
      const previousIndicators = queryClient.getQueryData<PageIndicator[]>(["/api/pages", currentPageData?.id, "indicators"]);

      // Optimistically update to the new value
      queryClient.setQueryData<PageIndicator[]>(
        ["/api/pages", currentPageData?.id, "indicators"],
        (old) => {
          if (!old) return old;
          return old.map(indicator => 
            indicator.id === indicatorId 
              ? { ...indicator, status }
              : indicator
          );
        }
      );

      // Also update the activeIndicator state optimistically
      if (activeIndicator && activeIndicator.id === indicatorId) {
        setActiveIndicator({ ...activeIndicator, status });
      }

      return { previousIndicators };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, roll back
      if (context?.previousIndicators) {
        queryClient.setQueryData(
          ["/api/pages", currentPageData?.id, "indicators"],
          context.previousIndicators
        );
      }
      
      // Also rollback activeIndicator state
      if (activeIndicator && activeIndicator.id === variables.indicatorId) {
        const previousIndicator = context?.previousIndicators?.find(ind => ind.id === variables.indicatorId);
        if (previousIndicator) {
          setActiveIndicator(previousIndicator);
        }
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      if (currentPageData) {
        invalidateIndicatorCaches(currentPageData.id, documentId, versionNumber, versionId);
      }
    }
  });

  const updateIndicatorDrawingBoxMutation = useMutation({
    mutationFn: async ({ indicatorId, drawingBox }: { 
      indicatorId: string; 
      drawingBox: any | null 
    }) => {
      const response = await fetch(`/api/indicators/${indicatorId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ drawingBox }),
      });
      if (!response.ok) throw new Error("Failed to update indicator drawing box");
      return response.json();
    },
    onMutate: async ({ indicatorId, drawingBox }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["/api/pages", currentPageData?.id, "indicators"] });

      // Snapshot the previous value
      const previousIndicators = queryClient.getQueryData<PageIndicator[]>(["/api/pages", currentPageData?.id, "indicators"]);

      // Optimistically update to the new value
      queryClient.setQueryData<PageIndicator[]>(
        ["/api/pages", currentPageData?.id, "indicators"],
        (old) => {
          if (!old) return old;
          return old.map(indicator => 
            indicator.id === indicatorId 
              ? { ...indicator, drawingBox }
              : indicator
          );
        }
      );

      // Also update the activeIndicator state optimistically
      if (activeIndicator && activeIndicator.id === indicatorId) {
        setActiveIndicator({ ...activeIndicator, drawingBox });
      }

      return { previousIndicators };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, roll back
      if (context?.previousIndicators) {
        queryClient.setQueryData(
          ["/api/pages", currentPageData?.id, "indicators"],
          context.previousIndicators
        );
      }
      
      // Also rollback activeIndicator state
      if (activeIndicator && activeIndicator.id === variables.indicatorId) {
        const previousIndicator = context?.previousIndicators?.find(ind => ind.id === variables.indicatorId);
        if (previousIndicator) {
          setActiveIndicator(previousIndicator);
        }
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      if (currentPageData) {
        invalidateIndicatorCaches(currentPageData.id, documentId, versionNumber, versionId);
      }
    }
  });

  return {
    updateIndicatorMutation,
    deleteIndicatorMutation,
    updateIndicatorStatusMutation,
    updateIndicatorDrawingBoxMutation,
  };
}